import React from 'react';

/**
 * Utility to retry lazy-loaded component imports with exponential backoff.
 * Helps recover from transient network failures and chunk loading errors.
 *
 * Usage:
 * const LazyComponent = lazy(() => lazyRetry(() => import('./Component')));
 */

const RETRY_DELAY_BASE = 1000; // Start with 1 second
const MAX_RETRIES = 3;

export function lazyRetry<T>(
  importFunction: () => Promise<T>,
  retries = MAX_RETRIES,
  interval = RETRY_DELAY_BASE
): Promise<T> {
  return new Promise((resolve, reject) => {
    importFunction()
      .then(resolve)
      .catch(error => {
        // Check if it's a chunk loading error
        const isChunkError =
          error?.name === 'ChunkLoadError' ||
          error?.message?.includes('Loading chunk') ||
          error?.message?.includes('Failed to fetch');

        if (!isChunkError) {
          // Not a chunk error, reject immediately
          reject(error);
          return;
        }

        // If we have retries left, try again with exponential backoff
        if (retries > 0) {
          console.log(
            `Chunk loading failed. Retrying in ${interval}ms... (${retries} attempts remaining)`
          );

          setTimeout(() => {
            lazyRetry(importFunction, retries - 1, interval * 2)
              .then(resolve)
              .catch(reject);
          }, interval);
        } else {
          // Out of retries
          console.error('Chunk loading failed after all retries. Reloading page...');

          // Force a full page reload to get fresh chunks
          setTimeout(() => {
            window.location.reload();
          }, 1000);

          reject(error);
        }
      });
  });
}

/**
 * Enhanced lazy loader with automatic retry for chunk errors
 *
 * Usage:
 * const LazyComponent = lazyWithRetry(() => import('./Component'));
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFunction: () => Promise<{ default: T }>
) {
  return React.lazy(() => lazyRetry(importFunction));
}

export default lazyWithRetry;
